/**
 * Word Card Component
 * Display vocabulary word with American and British phonetics
 */

import { useState } from 'react';
import { VocabularyWord } from '../../data/vocabulary';
import { usePronunciation } from '../../hooks/usePronunciation';

interface WordCardProps {
  word: VocabularyWord;
  showTranslation?: boolean;
  onLearned?: (word: VocabularyWord) => void;
}

export default function WordCard({ word, showTranslation = true, onLearned }: WordCardProps) {
  const { playingAccent, playPronunciation, playNormal } = usePronunciation();
  const [showDetails, setShowDetails] = useState(false);
  const [playingExample, setPlayingExample] = useState(false);

  // 智能推断年级和学段
  const getWordInfo = () => {
    // 如果有明确的字段，直接使用
    if (word.grade && word.level) {
      return { grade: word.grade, level: word.level };
    }

    // 否则根据分类和难度推断
    const elementaryCategories = ['animals', 'food', 'family', 'colors', 'numbers', 'body', 'actions', 'school', 'weather', 'emotions', 'daily_life', 'transport', 'places', 'time', 'clothes', 'sports'];
    const isElementary = elementaryCategories.includes(word.category);

    if (!word.level) {
      word.level = isElementary ? 'elementary' : 'middle';
    }

    if (!word.grade) {
      if (isElementary) {
        if (word.difficulty === 'easy') {
          const grade3Categories = ['animals', 'family', 'colors', 'numbers', 'body', 'food'];
          word.grade = grade3Categories.includes(word.category) ? '3' : '4';
        } else if (word.difficulty === 'medium') {
          const grade5Categories = ['daily_life', 'transport', 'places', 'time'];
          const grade6Categories = ['clothes', 'sports', 'weather', 'emotions'];
          if (grade5Categories.includes(word.category)) word.grade = '5';
          else if (grade6Categories.includes(word.category)) word.grade = '6';
          else word.grade = '5';
        } else {
          word.grade = '6';
        }
      } else {
        // 初中
        if (word.difficulty === 'easy') word.grade = '7';
        else if (word.difficulty === 'medium') word.grade = '7';
        else word.grade = '9';
      }
    }

    return { grade: word.grade!, level: word.level! };
  };

  const wordInfo = getWordInfo();

  const handlePlayExample = async () => {
    if (playingExample) return;
    setPlayingExample(true);
    try {
      await playNormal(word.example);
    } finally {
      setPlayingExample(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-soft p-6 hover:shadow-lg transition-all">
      {/* Header with image and word */}
      <div className="flex items-start gap-4 mb-4">
        {word.image && (
          <div className="text-5xl">{word.image}</div>
        )}
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-2xl font-bold text-gray-800">{word.word}</h3>
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              wordInfo.level === 'elementary' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
            }`}>
              {wordInfo.level === 'elementary' ? '小学' : '初中'}
              {' '}{wordInfo.grade}年级
            </span>
          </div>

          {/* Phonetics - American and British */}
          <div className="space-y-2">
            {/* American English */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => playPronunciation(word.word, 'american')}
                className={`flex items-center gap-1 px-3 py-1 rounded-lg transition-colors ${
                  playingAccent === 'american'
                    ? 'bg-blue-500 text-white'
                    : 'bg-blue-50 hover:bg-blue-100 text-blue-700'
                }`}
                title="播放美式发音"
              >
                <span className="text-lg">🇺🇸</span>
                <span className="text-sm">美式</span>
                <span className="text-lg">
                  {playingAccent === 'american' ? '🔊' : '▶️'}
                </span>
              </button>
              <span className="text-sm font-mono text-gray-600">
                {word.phoneticUS || word.phonetic || '/-/'}
              </span>
            </div>

            {/* British English */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => playPronunciation(word.word, 'british')}
                className={`flex items-center gap-1 px-3 py-1 rounded-lg transition-colors ${
                  playingAccent === 'british'
                    ? 'bg-purple-500 text-white'
                    : 'bg-purple-50 hover:bg-purple-100 text-purple-700'
                }`}
                title="播放英式发音"
              >
                <span className="text-lg">🇬🇧</span>
                <span className="text-sm">英式</span>
                <span className="text-lg">
                  {playingAccent === 'british' ? '🔊' : '▶️'}
                </span>
              </button>
              <span className="text-sm font-mono text-gray-600">
                {word.phoneticUK || word.phonetic || '/-/'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Meaning */}
      <div className="mb-4">
        <p className="text-lg text-gray-700 font-medium">{word.meaning}</p>
      </div>

      {/* Example sentence */}
      {showTranslation && (
        <div className="bg-gray-50 rounded-xl p-4 mb-3">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <p className="text-gray-700 mb-1">📝 {word.example}</p>
              {word.exampleTranslation && (
                <p className="text-sm text-gray-500">{word.exampleTranslation}</p>
              )}
            </div>
            <button
              onClick={handlePlayExample}
              disabled={playingExample}
              className={`flex-shrink-0 px-4 py-2 rounded-lg transition-colors ${
                playingExample
                  ? 'bg-green-500 text-white'
                  : 'bg-green-50 hover:bg-green-100 text-green-700'
              }`}
              title="播放例句"
            >
              <span className="text-lg">
                {playingExample ? '🔊' : '▶️'}
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Difficulty and Actions */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            word.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
            word.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
            'bg-red-100 text-red-700'
          }`}>
            {word.difficulty === 'easy' ? '简单' : word.difficulty === 'medium' ? '中等' : '困难'}
          </span>
        </div>

        <button
          onClick={() => setShowDetails(!showDetails)}
          className="text-sm text-primary hover:underline"
        >
          {showDetails ? '收起' : '详情'}
        </button>
      </div>

      {/* Detailed information */}
      {showDetails && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-gray-500">分类：</span>
              <span className="text-gray-700">{word.category}</span>
            </div>
            {word.grade && (
              <div>
                <span className="text-gray-500">年级：</span>
                <span className="text-gray-700">{word.grade}年级</span>
              </div>
            )}
            {word.level && (
              <div>
                <span className="text-gray-500">学段：</span>
                <span className="text-gray-700">{word.level === 'elementary' ? '小学' : '初中'}</span>
              </div>
            )}
          </div>

          {onLearned && (
            <button
              onClick={() => onLearned(word)}
              className="mt-3 w-full py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              我学会了 ✅
            </button>
          )}
        </div>
      )}
    </div>
  );
}
